import { useEffect, useState, type CSSProperties } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { SkilloLogo } from '@/components/SkilloLogo'
import { buildCounsellingWhatsAppUrl, siteConfig } from '@/lib/siteConfig'
import { IconArrowUpRight, IconChevronRight, IconMenu, IconX } from '@/components/Icons'
import { getEnrollment } from '@/lib/intake'
import { useCurrentTime } from '@/lib/hydration'
import { CONTACT_PATH } from '@/lib/portal/paths'
import { cn } from '@/lib/utils'

type NavItem = { to: string; label: string; end?: boolean }

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const counsellingUrl = buildCounsellingWhatsAppUrl(now)

  const items: NavItem[] = [
    { to: '/', label: 'Home', end: true },
    { to: '/courses', label: 'Courses' },
    { to: siteConfig.campaign.path, label: enrollment.batchLabel },
    { to: '/sylhet/freelancing-ai-web-development-courses', label: 'Sylhet' },
    { to: '/guides', label: 'Guides' },
    { to: '/about', label: 'About' },
    { to: CONTACT_PATH, label: 'Contact' },
  ]
  const menuItems: NavItem[] = [
    ...items.slice(0, 3),
    { to: '/free-career-orientation', label: 'Free Orientation' },
    { to: '/sylhet/freelancing-ai-web-development-courses', label: 'Courses in Sylhet' },
    { to: '/free-career-counselling', label: 'Free Counselling' },
    { to: '/guides', label: 'Guides' },
    { to: '/about', label: 'About' },
    { to: CONTACT_PATH, label: 'Contact' },
  ]

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 12)
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])

  useEffect(() => {
    if (!menuOpen) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [menuOpen])

  const raised = scrolled || menuOpen

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-[100] px-3 pt-3 sm:px-4">
        <div
          className={cn(
            'mx-auto flex h-14 max-w-[1240px] items-center justify-between gap-4 rounded-2xl border pl-3 pr-2 transition-[background-color,border-color,box-shadow] duration-500 sm:pl-4',
            raised
              ? 'border-white/[0.08] bg-[rgba(10,12,15,0.78)] shadow-[0_18px_50px_-24px_rgba(0,0,0,0.9)] backdrop-blur-xl'
              : 'border-transparent bg-transparent',
          )}
        >
          <div className="flex min-w-0 items-center gap-5">
            <Link
              to="/"
              className="shrink-0 rounded-lg no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]"
              aria-label="Skillo Academy home"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              }}
            >
              <SkilloLogo variant="academy" height={30} />
            </Link>
            <span aria-hidden="true" className="hidden h-5 w-px bg-white/10 lg:block" />
            <nav aria-label="Main" className="hidden lg:block">
              <ul className="flex items-center gap-0.5">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'relative inline-flex h-9 items-center rounded-full px-3 text-[13.5px] font-medium no-underline transition-colors duration-200',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]',
                          isActive
                            ? 'text-[var(--text-strong)]'
                            : 'text-white/60 hover:bg-white/[0.05] hover:text-white',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {item.label}
                          {isActive && (
                            <span
                              aria-hidden="true"
                              className="absolute inset-x-3 -bottom-[9px] h-px bg-[var(--color-brand-accent)] shadow-[0_0_14px_2px_rgba(0,229,255,0.7)]"
                            />
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Link
              to="/portal/login"
              className="hidden min-h-10 items-center rounded-full px-4 text-[13.5px] font-semibold text-white/75 no-underline transition-colors hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)] sm:inline-flex"
            >
              Sign in
            </Link>
            <a
              href={counsellingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative hidden min-h-10 items-center gap-1.5 overflow-hidden rounded-xl bg-[var(--color-brand-accent)] px-4 text-[13.5px] font-semibold text-[var(--accent-ink)] no-underline shadow-[0_8px_30px_-10px_rgba(0,229,255,0.7)] transition-[box-shadow,transform] duration-300 hover:-translate-y-px hover:shadow-[0_12px_38px_-8px_rgba(0,229,255,0.85)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:inline-flex"
            >
              <span
                aria-hidden="true"
                className="shine-sweep pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/45 to-transparent"
              />
              WhatsApp us
              <IconArrowUpRight
                size={14}
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:-translate-y-px group-hover:translate-x-px"
              />
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="inline-flex size-10 items-center justify-center rounded-xl border border-white/12 bg-white/[0.03] text-white transition-colors hover:bg-white/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)] lg:hidden"
            >
              {menuOpen ? (
                <IconX size={18} aria-hidden="true" />
              ) : (
                <IconMenu size={18} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <>
          <button
            type="button"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default border-0 bg-[var(--surface-overlay)] backdrop-blur-sm lg:hidden"
            aria-label="Close navigation menu"
            onClick={() => setMenuOpen(false)}
          />
          <div
            id="site-menu"
            className="fixed inset-x-3 top-[76px] z-50 max-h-[calc(100dvh-92px)] overflow-y-auto rounded-2xl border border-white/10 bg-[rgba(10,12,15,0.96)] p-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)] backdrop-blur-xl sm:inset-x-4 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <nav aria-label="Menu">
              <ul className="flex flex-col gap-0.5">
                {menuItems.map((item, index) => (
                  <li
                    key={`${item.to}-${item.label}`}
                    className="animate-blur-rise"
                    style={{ '--rise-delay': `${index * 35}ms` } as CSSProperties}
                  >
                    <Link
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      className="flex min-h-12 items-center justify-between rounded-xl px-3 text-[15px] font-medium text-white/85 no-underline hover:bg-white/[0.06] hover:text-white"
                    >
                      {item.label}
                      <IconChevronRight size={16} aria-hidden="true" className="text-white/35" />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-3 grid gap-2 border-t border-white/[0.06] pt-3 sm:grid-cols-2">
              <Link
                to="/portal/login"
                onClick={() => setMenuOpen(false)}
                className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 font-semibold text-white no-underline hover:bg-white/[0.06]"
              >
                Sign in
              </Link>
              <a
                href={counsellingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center justify-center gap-1.5 rounded-xl bg-[var(--color-brand-accent)] font-semibold text-[var(--accent-ink)] no-underline"
              >
                WhatsApp us <IconArrowUpRight size={14} aria-hidden="true" />
              </a>
            </div>
          </div>
        </>
      )}
    </>
  )
}
